import React, { useState, useEffect, useCallback } from 'react';
import { clinicorpApi, franchiseV2Api, metaApi, reportsApi, clinicDataApi } from '../services/api';
import useMeta from '../hooks/useMeta';
import toast from 'react-hot-toast';
import { getMetaBadgeClass, getMetaBadgeLabel, getMetaCardTheme } from '../utils/metaColor';
import ClinicDataSection from '../components/ClinicDataSection';
import {
  Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend,
  ResponsiveContainer, ComposedChart, Line
} from 'recharts';

/* ─── Formatters ─────────────────────────────────────────────────────────────*/
const fmtBRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');
const fmtPct = (v) => `${(Number(v) * 100).toFixed(1)}%`;
const fmtX   = (v) => v != null ? `${Number(v).toFixed(1)}x` : '—';

const today      = () => new Date().toISOString().split('T')[0];
const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

/* ─── KPI chip colours ───────────────────────────────────────────────────────*/
const roiColor = (roi) => {
  if (roi == null) return 'text-slate-400';
  if (roi >= 10) return 'text-emerald-600 font-bold';
  if (roi >= 5)  return 'text-amber-600 font-bold';
  return 'text-red-500 font-bold';
};

const cplColor = (cpl) => {
  if (cpl == null) return 'text-slate-400';
  if (cpl < 80)  return 'text-emerald-600';
  if (cpl < 150) return 'text-amber-600';
  return 'text-red-500';
};

// Badge de meta com as faixas: 0-40 vermelho | 41-60 amarelo | 61-80 verde
// claro | 81-99 verde escuro | 100+ "Meta Batida". `pct` chega como fração
// (0-1); getMetaBadgeClass/Label trabalham em percentual (0-100).
function GoalBar({ pct }) {
  if (pct == null) return <span className="text-slate-300 text-xs">—</span>;
  const p = pct * 100;
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold whitespace-nowrap ${getMetaBadgeClass(p)}`}>
      {getMetaBadgeLabel(p)}
    </span>
  );
}

/* ─── CSV export ─────────────────────────────────────────────────────────────*/
const exportCSV = (units, filename) => {
  const cols = [
    'Unidade','Investimento','Conversas','Leads','Total Conv.','CPL',
    'Recebido','Despesas','Resultado','ROI',
    'Consultas (Manual)', 'Agend. (Manual)', 'Comp. (Manual)', 'Fech. (Manual)',
    'Consultas (Clinicorp)','Conv%','Ticket Médio','Orç. Total','Orç. Aprovados',
    'Meta Orçamentos','Resultado Meta %',
    'Agendamentos (Clinicorp)','Faltas (Clinicorp)','Leads LP','Total Leads'
  ];
  const rows = units.map(u => [
    u.unityName,
    u.spend.toFixed(2),
    u.messages, u.leads, u.totalConversions,
    u.cpl != null ? u.cpl.toFixed(2) : '',
    u.revenue.toFixed(2), u.expenses.toFixed(2), u.result.toFixed(2),
    u.roi != null ? u.roi.toFixed(2) : '',
    u.routineLeads || 0, u.routineAppointments || 0, u.routineAttendances || 0, u.routineClosings || 0,
    u.appointmentsTotal,
    (u.conversionRate * 100).toFixed(1),
    u.ticketAverage.toFixed(2),
    u.estimatesTotalQty, u.estimatesApprovedQty,
    u.estimatesGoal.toFixed(2),
    u.estimatesGoalResult != null ? (u.estimatesGoalResult * 100).toFixed(1) : '',
    u.appointmentsTotal, u.appointmentsMissed, u.landingPageLeads, u.totalMarketingLeads,
  ]);
  const bom = '﻿';
  const body = [cols, ...rows].map(r => r.map(v => String(v).replace(/;/g, ',')).join(';')).join('\n');
  const blob = new Blob([bom + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

/* ─── Preset date helpers ────────────────────────────────────────────────────*/
const DATE_PRESETS = [
  { value: 'current_month', label: 'Mês Atual' },
  { value: 'last_7d',   label: 'Últimos 7 dias' },
  { value: 'last_14d',  label: 'Últimos 14 dias' },
  { value: 'last_30d',  label: 'Últimos 30 dias' },
  { value: 'custom',    label: 'Personalizado' },
];

function resolvePreset(preset) {
  const d = new Date();
  if (preset === 'current_month') {
    // Clinicorp only returns the configured revenue goal (approved_estimates)
    // when the query's "to" reaches the end of the month — a range ending
    // today (mid-month) comes back with the goal missing even though the
    // realized numbers are still correct up to today. Always send the full
    // month; Clinicorp naturally caps realized data at today regardless.
    return {
      startDate: new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0],
      endDate: new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0],
    };
  }
  const days = preset === 'last_7d' ? 7 : preset === 'last_14d' ? 14 : 30;
  const s = new Date(d); s.setDate(s.getDate() - days);
  return { startDate: s.toISOString().split('T')[0], endDate: d.toISOString().split('T')[0] };
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Top 5 — ranking de unidades por % de meta batida                          */
/* ═══════════════════════════════════════════════════════════════════════════ */
function TopUnitsRanking({ units }) {
  const ranked = units
    .filter(u => u.estimatesGoal > 0)
    .map(u => ({ ...u, pct: (u.estimatesGoalResult ?? 0) * 100 }))
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 5);

  if (ranked.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
      <h3 className="font-headline font-bold flex items-center gap-2 mb-4">
        <span className="material-symbols-outlined text-primary text-[20px]">leaderboard</span>
        Top 5 Unidades em Destaque
        <span className="text-xs font-normal text-slate-400 ml-1">% da meta batida</span>
      </h3>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {ranked.map((u, i) => {
          const theme = getMetaCardTheme(u.pct);
          return (
            <div key={u.unityName} className={`relative rounded-xl p-4 border ${theme.bg} ${theme.border}`}>
              <span className="absolute top-3 right-3 text-xl font-black text-slate-300/70">{i + 1}º</span>
              <p className="font-bold text-slate-800 text-xs truncate pr-6">{u.unityName}</p>
              <p className={`font-headline font-black text-lg mt-2 ${theme.value}`}>{getMetaBadgeLabel(u.pct)}</p>
              <p className={`text-[10px] mt-0.5 ${theme.sub}`}>{theme.msg}</p>
              <div className="mt-3 pt-3 border-t border-slate-200/60 text-[10px] text-slate-500 space-y-0.5">
                <div className="flex justify-between"><span>Receita</span><span className="font-medium text-slate-700">{fmtBRL(u.revenue)}</span></div>
                <div className="flex justify-between"><span>Meta</span><span className="font-medium text-slate-700">{fmtBRL(u.estimatesGoal)}</span></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Shared table bits (unidade cell + card wrapper) ───────────────────────*/
const NameCell = ({ unit }) => (
  <td className="px-3 py-3">
    <div className="flex items-center gap-2">
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${unit.hasMeta && unit.hasClinicorp ? 'bg-emerald-500' : unit.hasMeta ? 'bg-blue-500' : 'bg-amber-400'}`} />
      <span className="font-medium text-slate-800 text-xs whitespace-nowrap">{unit.unityName}</span>
    </div>
  </td>
);

function TableCard({ title, icon, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-headline font-bold flex items-center gap-2">
          <span className="material-symbols-outlined text-primary text-[20px]">{icon}</span>
          {title}
        </h3>
        <span className="text-xs text-slate-400">{subtitle}</span>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Result visualization                                                       */
/* ═══════════════════════════════════════════════════════════════════════════ */
export function ResultView({ units, period, topCampaigns, topAds, onBack, onShare, sharedLink, clinicDbIds, clinicSnapshotData, readOnly }) {
  const [sortField, setSortField] = useState('revenue');
  const [sortDir, setSortDir]     = useState('desc');
  const [expandedUnit, setExpandedUnit] = useState(null);

  const sorted = [...units].sort((a, b) => {
    const av = a[sortField] ?? -Infinity;
    const bv = b[sortField] ?? -Infinity;
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortTh = ({ field, children, className = '' }) => (
    <th
      onClick={() => toggleSort(field)}
      className={`px-3 py-3 font-bold text-[10px] uppercase tracking-widest cursor-pointer select-none whitespace-nowrap hover:text-primary transition-colors ${sortField === field ? 'text-primary' : 'text-slate-500'} ${className}`}
    >
      {children}
      {sortField === field && (
        <span className="ml-1 text-[9px]">{sortDir === 'desc' ? '▼' : '▲'}</span>
      )}
    </th>
  );

  const chartData = [...units].sort((a, b) => b.revenue - a.revenue).map(u => {
    return {
      name: u.unityName,
      gasto: +(u.spend).toFixed(2),
      conversoes: u.totalConversions,
      custoConversao: +(u.cpl || 0).toFixed(2),
      faturamento: +(u.revenue).toFixed(2),
      porcentagemMeta: u.estimatesGoal > 0 ? +((u.revenue / u.estimatesGoal) * 100).toFixed(2) : 0
    };
  });

  // Totals
  const totals = units.reduce((acc, u) => ({
    spend:                   acc.spend + u.spend,
    clicks:                  acc.clicks + u.clicks,
    impressions:             acc.impressions + u.impressions,
    messages:                acc.messages + u.messages,
    leads:                   acc.leads + u.leads,
    totalConversions:        acc.totalConversions + u.totalConversions,
    revenue:                 acc.revenue + u.revenue,
    received:                acc.received + u.received,
    expenses:                acc.expenses + u.expenses,
    result:                  acc.result + u.result,
    appointmentsTotal:       acc.appointmentsTotal + u.appointmentsTotal,
    appointmentsFinished:    acc.appointmentsFinished + u.appointmentsFinished,
    appointmentsMissed:      acc.appointmentsMissed + u.appointmentsMissed,
    appointmentsNew:         acc.appointmentsNew + u.appointmentsNew,
    estimatesApprovedAmount: acc.estimatesApprovedAmount + u.estimatesApprovedAmount,
    estimatesApprovedQty:    acc.estimatesApprovedQty + u.estimatesApprovedQty,
    estimatesGoal:           acc.estimatesGoal + u.estimatesGoal,
    landingPageLeads:        acc.landingPageLeads + u.landingPageLeads,
    totalMarketingLeads:     acc.totalMarketingLeads + u.totalMarketingLeads,
    routineLeads:            acc.routineLeads + (u.routineLeads || 0),
    routineAppointments:     acc.routineAppointments + (u.routineAppointments || 0),
    routineAttendances:      acc.routineAttendances + (u.routineAttendances || 0),
    routineClosings:         acc.routineClosings + (u.routineClosings || 0),
  }), {
    spend:0, clicks:0, impressions:0, messages:0, leads:0, totalConversions:0, revenue:0, received:0, expenses:0, result:0,
    appointmentsTotal:0, appointmentsFinished:0, appointmentsMissed:0, appointmentsNew:0,
    estimatesApprovedAmount:0, estimatesApprovedQty:0, estimatesGoal:0, landingPageLeads:0, totalMarketingLeads:0,
    routineLeads:0, routineAppointments:0, routineAttendances:0, routineClosings:0,
  });

  const globalCpl = totals.totalConversions > 0 ? totals.spend / totals.totalConversions : null;
  const globalRoi = totals.spend > 0 ? totals.received / totals.spend : null;
  // Atingido = receita / meta (não orçamentos aprovados — Clinicorp não
  // retorna mais esse campo com valor real, ver franchiseV2.js buildUnit()).
  const globalGoalResult = totals.estimatesGoal > 0 ? totals.revenue / totals.estimatesGoal : null;
  const globalCostPerAppt = totals.spend > 0 && totals.appointmentsTotal > 0 ? totals.spend / totals.appointmentsTotal : null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back + Export */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        {!readOnly ? (
          <button onClick={onBack}
            className="flex items-center gap-2 text-slate-500 hover:text-primary text-sm font-medium transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">arrow_back</span> Voltar à configuração
          </button>
        ) : <div />}
        <div className="flex gap-2">
          {!readOnly && (
            <button
              onClick={onShare}
              className="flex items-center gap-2 px-5 py-2 rounded-full font-bold bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm text-sm"
            >
              <span className="material-symbols-outlined text-sm">share</span> Compartilhar
            </button>
          )}
          <button
            onClick={() => exportCSV(units, `franquia_v2_${period.startDate}_${period.endDate}.csv`)}
            className="flex items-center gap-2 px-5 py-2 rounded-full font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm text-sm"
          >
            <span className="material-symbols-outlined text-sm">download</span> Exportar CSV
          </button>
        </div>
      </div>

      {/* Top 5 destaques */}
      <TopUnitsRanking units={units} />

      {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1">Unidades</p>
            <p className="font-headline font-black text-2xl text-slate-800">{units.length}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1">Investimento</p>
            <p className="font-headline font-black text-lg text-slate-800">{fmtBRL(totals.spend)}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1">Conversas</p>
            <p className="font-headline font-black text-2xl text-slate-800">{fmtNum(totals.messages)}</p>
            {globalCpl && <p className="text-[10px] text-slate-400 mt-0.5">CPL {fmtBRL(globalCpl)}</p>}
          </div>
          <div className="primary-pill-gradient rounded-2xl p-4 shadow-lg shadow-primary/20 text-white">
            <p className="text-[9px] font-bold uppercase tracking-widest text-white/70 mb-1">Recebido</p>
            <p className="font-headline font-black text-lg">{fmtBRL(totals.revenue)}</p>
            {globalGoalResult != null && (
              <p className="text-[10px] text-white/80 mt-0.5">Meta {fmtPct(globalGoalResult)}</p>
            )}
          </div>
          <div className="bg-emerald-600 rounded-2xl p-4 shadow-lg text-white">
            <p className="text-[9px] font-bold uppercase tracking-widest text-white/70 mb-1">Resultado</p>
            <p className="font-headline font-black text-lg">{fmtBRL(totals.result)}</p>
          </div>
          <div className={`rounded-2xl p-4 shadow-lg text-white ${globalRoi != null && globalRoi >= 5 ? 'bg-blue-600' : 'bg-slate-700'}`}>
            <p className="text-[9px] font-bold uppercase tracking-widest text-white/70 mb-1">ROI Global</p>
            <p className="font-headline font-black text-2xl">{fmtX(globalRoi)}</p>
          </div>
        </div>

      {/* Charts Section */}
      {chartData.length > 0 && (
        <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          
          {/* Desempenho Contas */}
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
            <h3 className="font-headline font-bold mb-4 flex items-center gap-2"><span className="material-symbols-outlined text-primary text-[20px]">bar_chart</span> Desempenho Unidades</h3>
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} tickFormatter={(v) => `R$${v}`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} />
                  <RechartsTooltip 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                    labelStyle={{ fontWeight: 'bold', color: '#1e293b', marginBottom: '4px' }}
                    formatter={(value, name) => [name === 'Gasto Total (R$)' ? fmtBRL(value) : value, name]}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '12px' }}/>
                  <Bar yAxisId="left" dataKey="gasto" name="Gasto Total (R$)" fill="#cd19f9" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="conversoes" name="Total Conv." stroke="#0ea5e9" strokeWidth={3} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
  
          {/* Conversões vs Custo vs Faturamento */}
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
            <h3 className="font-headline font-bold mb-4 flex items-center gap-2"><span className="material-symbols-outlined text-primary text-[20px]">show_chart</span> Conversões x Recebido</h3>
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} tickFormatter={(v) => `R$${v}`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} />
                  <RechartsTooltip 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                    labelStyle={{ fontWeight: 'bold', color: '#1e293b', marginBottom: '4px' }}
                    formatter={(value, name) => [
                       name === 'Qtd Conversões' ? value : fmtBRL(value), 
                       name
                    ]}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '12px' }}/>
                  <Bar yAxisId="left" dataKey="faturamento" name="Recebido (R$)" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="left" type="monotone" dataKey="custoConversao" name="Custo / Conv (R$)" stroke="#ef4444" strokeWidth={3} />
                  <Line yAxisId="right" type="monotone" dataKey="conversoes" name="Qtd Conversões" stroke="#0ea5e9" strokeWidth={3} activeDot={{ r: 6 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
  
        </section>
      )}

      {/* Tabela 1 - Anuncios e Resultado */}
      <TableCard title="Anúncios & Resultado" icon="ads_click" subtitle={`${units.length} unidades`}>
        <table className="w-full text-left text-sm font-body">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">Unidade</th>
              <SortTh field="spend">Invest.</SortTh>
              <SortTh field="messages">Conversas</SortTh>
              <SortTh field="leads">Leads</SortTh>
              <SortTh field="clicks">Cliques</SortTh>
              <SortTh field="impressions">Impressões</SortTh>
              <SortTh field="cpl">CPL</SortTh>
              <SortTh field="costPerAppt">Custo/Consulta</SortTh>
              <SortTh field="roi">ROI</SortTh>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map((unit) => (
              <tr key={unit.unityName} className="hover:bg-slate-50 transition-colors">
                <NameCell unit={unit} />
                <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs">{fmtBRL(unit.spend)}</td>
                <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs">{fmtNum(unit.messages)}</td>
                <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs">{fmtNum(unit.leads)}</td>
                <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">{fmtNum(unit.clicks)}</td>
                <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">{fmtNum(unit.impressions)}</td>
                <td className={`px-3 py-3 whitespace-nowrap text-xs ${cplColor(unit.cpl)}`}>
                  {unit.cpl != null ? fmtBRL(unit.cpl) : '—'}
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">
                  {unit.costPerAppt != null ? fmtBRL(unit.costPerAppt) : '—'}
                </td>
                <td className={`px-3 py-3 whitespace-nowrap text-xs ${roiColor(unit.roi)}`}>{fmtX(unit.roi)}</td>
              </tr>
            ))}
            <tr className="bg-slate-100 font-bold border-t-2 border-slate-200 text-xs">
              <td className="px-3 py-3 text-slate-600 uppercase tracking-wider">TOTAL</td>
              <td className="px-3 py-3 text-slate-800">{fmtBRL(totals.spend)}</td>
              <td className="px-3 py-3 text-slate-800">{fmtNum(totals.messages)}</td>
              <td className="px-3 py-3 text-slate-800">{fmtNum(totals.leads)}</td>
              <td className="px-3 py-3 text-slate-800">{fmtNum(totals.clicks)}</td>
              <td className="px-3 py-3 text-slate-800">{fmtNum(totals.impressions)}</td>
              <td className={`px-3 py-3 ${cplColor(globalCpl)}`}>{globalCpl ? fmtBRL(globalCpl) : '—'}</td>
              <td className="px-3 py-3 text-slate-800">{globalCostPerAppt != null ? fmtBRL(globalCostPerAppt) : '—'}</td>
              <td className={`px-3 py-3 ${roiColor(globalRoi)}`}>{fmtX(globalRoi)}</td>
            </tr>
          </tbody>
        </table>
      </TableCard>

      {/* Tabela 2 — Faturamento, Metas & Leads Recebidos */}
      <TableCard title="Faturamento, Metas & Leads Recebidos" icon="paid" subtitle={`${units.length} unidades`}>
        <table className="w-full text-left text-sm font-body">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">Unidade</th>
              <SortTh field="revenue">Recebido</SortTh>
              <SortTh field="estimatesGoal">Meta</SortTh>
              <SortTh field="estimatesGoalResult">Atingido</SortTh>
              <SortTh field="expenses">Despesas</SortTh>
              <SortTh field="result">Resultado</SortTh>
              <SortTh field="estimatesApprovedAmount">Orç. Aprovados</SortTh>
              <SortTh field="ticketAverage">Ticket Médio</SortTh>
              <SortTh field="landingPageLeads">Leads LP</SortTh>
              <SortTh field="totalMarketingLeads">Total Leads Mkt.</SortTh>
              <SortTh field="appointmentsTotal">Consultas</SortTh>
              <SortTh field="appointmentsNew">Novos Pacientes</SortTh>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map((unit) => (
              <tr key={unit.unityName} className="hover:bg-slate-50 transition-colors">
                <NameCell unit={unit} />
                <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs font-medium">{fmtBRL(unit.revenue)}</td>
                <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">
                  {unit.estimatesGoal > 0 ? fmtBRL(unit.estimatesGoal) : '—'}
                </td>
                <td className="px-3 py-3 whitespace-nowrap">
                  <GoalBar pct={unit.estimatesGoalResult} />
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-red-500 text-xs">{fmtBRL(unit.expenses)}</td>
                <td className={`px-3 py-3 whitespace-nowrap text-xs font-medium ${unit.result >= 0 ? 'text-blue-700' : 'text-red-600'}`}>
                  {fmtBRL(unit.result)}
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">
                  {fmtBRL(unit.estimatesApprovedAmount)} <span className="text-slate-400">({unit.estimatesApprovedQty})</span>
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">
                  {unit.ticketAverage > 0 ? fmtBRL(unit.ticketAverage) : '—'}
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">{unit.pageId ? fmtNum(unit.landingPageLeads) : '—'}</td>
                <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">{fmtNum(unit.totalMarketingLeads)}</td>
                <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">{fmtNum(unit.appointmentsTotal)}</td>
                <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">{fmtNum(unit.appointmentsNew)}</td>
              </tr>
            ))}
            <tr className="bg-slate-100 font-bold border-t-2 border-slate-200 text-xs">
              <td className="px-3 py-3 text-slate-600 uppercase tracking-wider">TOTAL</td>
              <td className="px-3 py-3 text-slate-800">{fmtBRL(totals.revenue)}</td>
              <td className="px-3 py-3 text-slate-800">{totals.estimatesGoal > 0 ? fmtBRL(totals.estimatesGoal) : '—'}</td>
              <td className="px-3 py-3"><GoalBar pct={globalGoalResult} /></td>
              <td className="px-3 py-3 text-red-600">{fmtBRL(totals.expenses)}</td>
              <td className={`px-3 py-3 ${totals.result >= 0 ? 'text-blue-700' : 'text-red-600'}`}>{fmtBRL(totals.result)}</td>
              <td className="px-3 py-3 text-slate-800">{fmtBRL(totals.estimatesApprovedAmount)} <span className="text-slate-500 font-normal">({totals.estimatesApprovedQty})</span></td>
              <td className="px-3 py-3 text-slate-500 font-normal">—</td>
              <td className="px-3 py-3 text-slate-800">{fmtNum(totals.landingPageLeads)}</td>
              <td className="px-3 py-3 text-slate-800">{fmtNum(totals.totalMarketingLeads)}</td>
              <td className="px-3 py-3 text-slate-800">{fmtNum(totals.appointmentsTotal)}</td>
              <td className="px-3 py-3 text-slate-800">{fmtNum(totals.appointmentsNew)}</td>
            </tr>
          </tbody>
        </table>
      </TableCard>

      {/* Tabela 3 — Visão Completa, com seções */}
      <TableCard title="Visão Completa" icon="table_chart" subtitle={`${period.startDate} → ${period.endDate}`}>
          <table className="w-full text-left text-sm font-body">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th rowSpan={2} className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap align-bottom">Unidade</th>
                <th colSpan={3} className="px-3 py-2 font-bold text-[9px] uppercase tracking-widest text-blue-600 bg-blue-50 text-center border-l-2 border-slate-200">Anúncios</th>
                <th colSpan={5} className="px-3 py-2 font-bold text-[9px] uppercase tracking-widest text-emerald-600 bg-emerald-50 text-center border-l-2 border-slate-200">Faturamento &amp; Metas</th>
                <th colSpan={7} className="px-3 py-2 font-bold text-[9px] uppercase tracking-widest text-amber-600 bg-amber-50 text-center border-l-2 border-slate-200">Leads &amp; Consultas</th>
                <th rowSpan={2} className="px-3 py-3 w-10"></th>
              </tr>
              <tr>
                <SortTh field="spend" className="border-l-2 border-slate-200">Invest.</SortTh>
                <SortTh field="messages">Conv.</SortTh>
                <SortTh field="cpl">CPL</SortTh>
                <SortTh field="revenue" className="border-l-2 border-slate-200">Recebido</SortTh>
                <SortTh field="estimatesGoal">Meta</SortTh>
                <SortTh field="estimatesGoalResult">Atingido</SortTh>
                <SortTh field="result">Resultado</SortTh>
                <SortTh field="roi">ROI</SortTh>
                <SortTh field="routineLeads" className="border-l-2 border-slate-200">Leads (Rot.)</SortTh>
                <SortTh field="routineAppointments">Agend. (Rot.)</SortTh>
                <SortTh field="routineAttendances">Comp. (Rot.)</SortTh>
                <SortTh field="routineClosings">Fech. (Rot.)</SortTh>
                <SortTh field="appointmentsTotal">Consultas</SortTh>
                <SortTh field="conversionRate">Conv%</SortTh>
                <SortTh field="ticketAverage">Ticket</SortTh>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map((unit) => (
                <React.Fragment key={unit.unityName}>
                  <tr
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => setExpandedUnit(expandedUnit === unit.unityName ? null : unit.unityName)}
                  >
                    <NameCell unit={unit} />
                    <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs border-l-2 border-slate-200">{fmtBRL(unit.spend)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs">{fmtNum(unit.messages)}</td>
                    <td className={`px-3 py-3 whitespace-nowrap text-xs ${cplColor(unit.cpl)}`}>
                      {unit.cpl != null ? fmtBRL(unit.cpl) : '—'}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs font-medium border-l-2 border-slate-200">{fmtBRL(unit.revenue)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">
                      {unit.estimatesGoal > 0 ? fmtBRL(unit.estimatesGoal) : '—'}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <GoalBar pct={unit.estimatesGoalResult} />
                    </td>
                    <td className={`px-3 py-3 whitespace-nowrap text-xs font-medium ${unit.result >= 0 ? 'text-blue-700' : 'text-red-600'}`}>
                      {fmtBRL(unit.result)}
                    </td>
                    <td className={`px-3 py-3 whitespace-nowrap text-xs ${roiColor(unit.roi)}`}>
                      {fmtX(unit.roi)}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs border-l-2 border-slate-200">{fmtNum(unit.routineLeads)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">{fmtNum(unit.routineAppointments)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">{fmtNum(unit.routineAttendances)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">{fmtNum(unit.routineClosings)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">{fmtNum(unit.appointmentsTotal)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">{fmtPct(unit.conversionRate)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs">
                      {unit.ticketAverage > 0 ? fmtBRL(unit.ticketAverage) : '—'}
                    </td>
                    <td className="px-3 py-3">
                      <span className="material-symbols-outlined text-slate-300 text-[16px]">
                        {expandedUnit === unit.unityName ? 'expand_less' : 'expand_more'}
                      </span>
                    </td>
                  </tr>

                  {/* Expanded row */}
                  {expandedUnit === unit.unityName && (
                    <tr>
                      <td colSpan={14} className="bg-slate-50 px-6 py-5 border-b border-slate-100">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                          {/* Meta block */}
                          <div className="bg-white rounded-xl p-4 border border-blue-100">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-blue-500 mb-3 flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> Meta Ads
                            </p>
                            <div className="space-y-1.5 text-xs">
                              <div className="flex justify-between"><span className="text-slate-500">Investimento</span><span className="font-medium">{fmtBRL(unit.spend)}</span></div>
                              <div className="flex justify-between"><span className="text-slate-500">Conversas</span><span className="font-medium">{fmtNum(unit.messages)}</span></div>
                              <div className="flex justify-between"><span className="text-slate-500">Leads</span><span className="font-medium">{fmtNum(unit.leads)}</span></div>
                              <div className="flex justify-between"><span className="text-slate-500">Cliques</span><span className="font-medium">{fmtNum(unit.clicks)}</span></div>
                              <div className="flex justify-between"><span className="text-slate-500">Impressões</span><span className="font-medium">{fmtNum(unit.impressions)}</span></div>
                              <div className="flex justify-between border-t border-slate-100 pt-1.5 mt-1.5">
                                <span className="text-slate-500">CPL</span>
                                <span className={`font-bold ${cplColor(unit.cpl)}`}>{unit.cpl != null ? fmtBRL(unit.cpl) : '—'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Revenue block */}
                          <div className="bg-white rounded-xl p-4 border border-emerald-100">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-500 mb-3">Financeiro</p>
                              <div className="space-y-1.5 text-xs">
                                <div className="flex justify-between"><span className="text-slate-500">Faturamento</span><span className="font-medium">{fmtBRL(unit.revenue)}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Despesas</span><span className="font-medium text-red-500">{fmtBRL(unit.expenses)}</span></div>
                                <div className="flex justify-between border-t border-slate-100 pt-1.5 mt-1.5">
                                  <span className="text-slate-500">Resultado</span>
                                  <span className={`font-bold ${unit.result >= 0 ? 'text-blue-600' : 'text-red-500'}`}>{fmtBRL(unit.result)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">ROI</span>
                                <span className={`font-bold ${roiColor(unit.roi)}`}>{fmtX(unit.roi)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Estimates block */}
                          <div className="bg-white rounded-xl p-4 border border-slate-100">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">
                              Orçamentos ({unit.estimatesTotalQty})
                            </p>
                            <div className="space-y-1.5 text-xs">
                              <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-medium">{fmtBRL(unit.estimatesTotalAmount)}</span></div>
                              <div className="flex justify-between text-emerald-600"><span>Aprovados ({unit.estimatesApprovedQty})</span><span className="font-medium">{fmtBRL(unit.estimatesApprovedAmount)}</span></div>
                              <div className="flex justify-between text-amber-600"><span>Em aberto ({unit.estimatesOpenQty})</span><span className="font-medium">{fmtBRL(unit.estimatesOpenAmount)}</span></div>
                              <div className="flex justify-between border-t border-slate-100 pt-1.5 mt-1.5">
                                <span className="text-slate-500">Ticket Médio</span>
                                <span className="font-medium">{unit.ticketAverage > 0 ? fmtBRL(unit.ticketAverage) : '—'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Recv/Lead</span>
                                <span className="font-medium">{unit.revenuePerLead != null ? fmtBRL(unit.revenuePerLead) : '—'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Appointments block */}
                          <div className="bg-white rounded-xl p-4 border border-slate-100">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">
                              Consultas ({fmtNum(unit.appointmentsTotal)})
                            </p>
                            <div className="space-y-1.5 text-xs">
                              <div className="flex justify-between"><span className="text-slate-500">Finalizadas</span><span className="font-medium text-emerald-600">{fmtNum(unit.appointmentsFinished)}</span></div>
                              <div className="flex justify-between"><span className="text-slate-500">Faltas</span><span className="font-medium text-red-500">{fmtNum(unit.appointmentsMissed)}</span></div>
                              <div className="flex justify-between"><span className="text-slate-500">Novos Pacientes</span><span className="font-medium text-blue-600">{fmtNum(unit.appointmentsNew)}</span></div>
                              <div className="flex justify-between border-t border-slate-100 pt-1.5 mt-1.5">
                                <span className="text-slate-500">Conv. Clínica</span>
                                <span className="font-medium">{fmtPct(unit.conversionRate)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Custo/Consulta</span>
                                <span className="font-medium">{unit.costPerAppt != null ? fmtBRL(unit.costPerAppt) : '—'}</span>
                              </div>
                              <div className="flex justify-between border-t border-slate-100 pt-1.5 mt-1.5">
                                <span className="text-slate-500">Leads LP</span>
                                <span className="font-medium">{unit.pageId ? fmtNum(unit.landingPageLeads) : '—'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}

              {/* Totals row */}
              <tr className="bg-slate-100 font-bold border-t-2 border-slate-200 text-xs">
                <td className="px-3 py-3 text-slate-600 uppercase tracking-wider">TOTAL</td>
                <td className="px-3 py-3 text-slate-800 border-l-2 border-slate-200">{fmtBRL(totals.spend)}</td>
                <td className="px-3 py-3 text-slate-800">{fmtNum(totals.messages)}</td>
                <td className={`px-3 py-3 ${cplColor(globalCpl)}`}>{globalCpl ? fmtBRL(globalCpl) : '—'}</td>
                <td className="px-3 py-3 text-slate-800 border-l-2 border-slate-200">{fmtBRL(totals.revenue)}</td>
                <td className="px-3 py-3 text-slate-800">{totals.estimatesGoal > 0 ? fmtBRL(totals.estimatesGoal) : '—'}</td>
                <td className="px-3 py-3"><GoalBar pct={globalGoalResult} /></td>
                <td className={`px-3 py-3 ${totals.result >= 0 ? 'text-blue-700' : 'text-red-600'}`}>{fmtBRL(totals.result)}</td>
                <td className={`px-3 py-3 ${roiColor(globalRoi)}`}>{fmtX(globalRoi)}</td>
                <td className="px-3 py-3 text-slate-800 border-l-2 border-slate-200">{fmtNum(totals.routineLeads)}</td>
                <td className="px-3 py-3 text-slate-800">{fmtNum(totals.routineAppointments)}</td>
                <td className="px-3 py-3 text-slate-800">{fmtNum(totals.routineAttendances)}</td>
                <td className="px-3 py-3 text-slate-800">{fmtNum(totals.routineClosings)}</td>
                <td className="px-3 py-3 text-slate-800">{fmtNum(totals.appointmentsTotal)}</td>
                <td colSpan={3} />
              </tr>
            </tbody>
          </table>

        {/* Legend */}
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex gap-5 text-[10px] text-slate-500">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Meta + Clinicorp</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-500" /> Somente Meta</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" /> Somente Clinicorp</span>
        </div>
      </TableCard>

      {topCampaigns?.length > 0 && (
        <TableCard title="Top 10 Campanhas" icon="campaign" subtitle="Principais campanhas por Conversões/Investimento">
          <table className="w-full text-left text-sm font-body">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">Unidade</th>
                <th className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">Campanha</th>
                <th className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">Invest.</th>
                <th className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">Conversas</th>
                <th className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">Leads</th>
                <th className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">CPL</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {topCampaigns.map((c, i) => (
                <tr key={c.campaign_id + i} className="hover:bg-slate-50 transition-colors">
                  <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs font-bold">{c.unitName}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs">{c.campaign_name}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs">{fmtBRL(c.spend)}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs">{fmtNum(c.messages)}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs">{fmtNum(c.leads)}</td>
                  <td className={`px-3 py-3 whitespace-nowrap text-xs ${cplColor(c.cpl)}`}>{c.cpl != null ? fmtBRL(c.cpl) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      )}

      {topAds?.length > 0 && (
        <TableCard title="Top 20 Criativos" icon="image" subtitle="Principais anúncios por Conversões/Investimento">
          <table className="w-full text-left text-sm font-body">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">Criativo</th>
                <th className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">Unidade</th>
                <th className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">Anúncio</th>
                <th className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">Invest.</th>
                <th className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">Conversas</th>
                <th className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">Leads</th>
                <th className="px-3 py-3 font-bold text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">CPL</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {topAds.map((a, i) => (
                <tr key={a.ad_id + i} className="hover:bg-slate-50 transition-colors">
                  <td className="px-3 py-2 whitespace-nowrap w-16">
                    {a.thumbnail_url ? (
                      <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden relative cursor-pointer hover:ring-2 hover:ring-primary transition-all">
                        <img src={a.thumbnail_url} alt="" className="w-full h-full object-cover" onClick={() => window.open(a.preview_shareable_link || a.image_url, '_blank')} />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-300">
                        <span className="material-symbols-outlined text-[20px]">image</span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs font-bold">{a.unitName}</td>
                  <td className="px-3 py-3 text-slate-700 text-xs">
                    <p className="font-medium truncate max-w-[200px]" title={a.ad_name}>{a.ad_name}</p>
                    <p className="text-[9px] text-slate-400 truncate max-w-[200px]" title={a.campaign_name}>{a.campaign_name}</p>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs">{fmtBRL(a.spend)}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs">{fmtNum(a.messages)}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-slate-700 text-xs">{fmtNum(a.leads)}</td>
                  <td className={`px-3 py-3 whitespace-nowrap text-xs ${cplColor(a.cpl)}`}>{a.cpl != null ? fmtBRL(a.cpl) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      )}

      {(clinicDbIds?.length > 0 || clinicSnapshotData) && (
        <ClinicDataSection 
          adAccountIds={!readOnly ? clinicDbIds : undefined} 
          startDate={!readOnly ? period.startDate : undefined} 
          endDate={!readOnly ? period.endDate : undefined} 
          data={readOnly ? clinicSnapshotData : undefined}
          readOnly={readOnly}
        />
      )}

    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Main Page                                                                  */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function FranchiseReportsV2() {
  const { accounts: metaAccounts, fetchAccounts } = useMeta();

  // Config state
  const [reportName,         setReportName]         = useState('Relatório Franquia V2');
  const [clinicorpAccountId, setClinicorpAccountId] = useState('');
  const [selectedMetaIds,    setSelectedMetaIds]    = useState([]);
  const [datePreset,         setDatePreset]         = useState('current_month');
  const [startDate,          setStartDate]          = useState(monthStart());
  const [endDate,            setEndDate]            = useState(today());

  // Mapping state — array of { unityName, metaAccountIds: [], ignored: false }
  const [mappings, setMappings] = useState([]);
  const [mappingsLoaded, setMappingsLoaded] = useState(false);

  // Data
  const [clinicorpAccounts, setClinicorpAccounts] = useState([]);
  const [savedReports,      setSavedReports]      = useState([]);
  const [currentReportId,   setCurrentReportId]   = useState(null);
  const [pages,             setPages]             = useState([]);

  // UI state
  const [view,            setView]            = useState('list'); // 'list' | 'edit' | 'result'
  const [loading,         setLoading]         = useState(false);
  const [savingMappings,  setSavingMappings]  = useState(false);
  const [generating,      setGenerating]      = useState(false);
  const [resultData,      setResultData]      = useState(null);
  const [resultPeriod,    setResultPeriod]    = useState(null);
  const [resultTopCampaigns, setResultTopCampaigns] = useState([]);
  const [resultTopAds, setResultTopAds] = useState([]);
  const [sharedLink, setSharedLink] = useState('');
  const [metaDropOpen,    setMetaDropOpen]    = useState(false);

  useEffect(() => {
    fetchAccounts();
    loadSavedReports();
    clinicorpApi.getAccounts()
      .then(r => setClinicorpAccounts(r.data.accounts || []))
      .catch(() => {});
    metaApi.getPages()
      .then(r => setPages(r.data?.pages || []))
      .catch(() => {});
  }, [fetchAccounts]);

  const loadSavedReports = async () => {
    try {
      const r = await franchiseV2Api.getReports();
      setSavedReports(r.data || []);
    } catch {}
  };

  // Load mappings when clinicorp account changes
  useEffect(() => {
    if (!clinicorpAccountId) { setMappings([]); setMappingsLoaded(false); return; }
    franchiseV2Api.getMappings(clinicorpAccountId)
      .then(r => {
        setMappings(r.data || []);
        setMappingsLoaded(true);
      })
      .catch(() => setMappingsLoaded(true));
  }, [clinicorpAccountId]);

  // Merge new Clinicorp units into mapping list (preserving saved data)
  const mergeUnitsIntoMappings = useCallback((units) => {
    setMappings(prev => {
      const existingNames = new Set(prev.map(m => m.unityName));
      const newEntries = units
        .filter(u => !existingNames.has(u))
        .map(u => ({ unityName: u, metaAccountIds: [], pageId: '', pageName: '', ignored: false }));
      return [...prev, ...newEntries];
    });
  }, []);

  const getDateRange = () => {
    if (datePreset === 'custom') return { startDate, endDate };
    return resolvePreset(datePreset);
  };

  const handleLoadUnits = async () => {
    if (!clinicorpAccountId) { toast.error('Selecione uma conta Clinicorp'); return; }
    const { startDate: sd, endDate: ed } = getDateRange();
    setLoading(true);
    try {
      const r = await clinicorpApi.getAnalytics(clinicorpAccountId, sd, ed);
      const rows = Array.isArray(r.data.results) ? r.data.results : [];
      const units = [...new Set(rows.map(row => row.UnityName).filter(Boolean))].sort();
      if (units.length === 0) { toast.error('Nenhuma unidade encontrada no período'); return; }
      mergeUnitsIntoMappings(units);
      toast.success(`${units.length} unidades carregadas`);
    } catch (err) {
      toast.error(err.message || 'Erro ao carregar unidades Clinicorp');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMappings = async () => {
    if (!clinicorpAccountId) return;
    setSavingMappings(true);
    try {
      await franchiseV2Api.saveMappings(clinicorpAccountId, mappings);
      toast.success('Mapeamentos salvos');
    } catch {
      toast.error('Erro ao salvar mapeamentos');
    } finally {
      setSavingMappings(false);
    }
  };

  const handleGenerate = async () => {
    if (!clinicorpAccountId) { toast.error('Selecione conta Clinicorp'); return; }
    const activeMappings = mappings.filter(m => !m.ignored && m.metaAccountIds.length > 0);
    if (activeMappings.length === 0) { toast.error('Mapeie ao menos uma unidade'); return; }

    const { startDate: sd, endDate: ed } = getDateRange();
    setGenerating(true);
    try {
      const r = await franchiseV2Api.generate({ clinicorpAccountId, startDate: sd, endDate: ed });
      setResultData(r.data.units);
      setResultTopCampaigns(r.data.topCampaigns || []);
      setResultTopAds(r.data.topAds || []);
      setResultPeriod(r.data.period);
      setView('result');
    } catch (err) {
      toast.error(err.message || 'Erro ao gerar relatório');
    } finally {
      setGenerating(false);
    }
  };

  const handleShareReport = async () => {
    try {
      const clinicDbIds = selectedMetaIds
        .map(id => metaAccounts.find(a => (a.account_id || a.metaAccountId) === id || a.id?.replace('act_', '') === id)?.id)
        .filter(Boolean);
      
      const { startDate: sd, endDate: ed } = getDateRange();
      let clinicSnapshot = null;
      if (clinicDbIds.length > 0) {
        clinicSnapshot = await clinicDataApi.getAccountData(clinicDbIds, sd, ed)
          .then(res => res.data || [])
          .catch(() => null);
      }

      const payload = {
        reportType: 'franchise_v2',
        entityId: currentReportId || 'temporario-franchise-v2',
        entityName: reportName,
        options: {
           units: resultData,
           topCampaigns: resultTopCampaigns,
           topAds: resultTopAds,
           period: resultPeriod,
           snapshotClinicData: clinicSnapshot,
        }
      };

      const res = await reportsApi.shareReport(payload);
      setSharedLink(res.data.url);
      
      navigator.clipboard.writeText(res.data.url)
        .then(() => toast.success('Link copiado para a área de transferência!'))
        .catch(() => toast.success(`Compartilhado: ${res.data.url}`));
        
    } catch (err) {
      toast.error('Erro ao criar link de compartilhamento.');
    }
  };

  const handleSaveReport = async () => {
    if (!clinicorpAccountId) return;
    const { startDate: sd, endDate: ed } = getDateRange();
    const payload = {
      name: reportName,
      clinicorpAccountId,
      metaAccountIds: selectedMetaIds,
      datePreset: datePreset !== 'custom' ? datePreset : null,
      startDate: datePreset === 'custom' ? sd : null,
      endDate: datePreset === 'custom' ? ed : null,
    };
    try {
      if (currentReportId) {
        await franchiseV2Api.updateReport(currentReportId, payload);
      } else {
        const r = await franchiseV2Api.createReport(payload);
        setCurrentReportId(r.data.id);
      }
      await loadSavedReports();
      toast.success('Relatório salvo');
    } catch {
      toast.error('Erro ao salvar relatório');
    }
  };

  const handleOpenSaved = async (rep) => {
    setCurrentReportId(rep.id);
    setReportName(rep.name);
    setClinicorpAccountId(rep.clinicorpAccountId);
    setSelectedMetaIds(rep.metaAccountIds || []);
    if (rep.datePreset) {
      setDatePreset(rep.datePreset);
    } else if (rep.startDate && rep.endDate) {
      setDatePreset('custom');
      setStartDate(rep.startDate.split('T')[0]);
      setEndDate(rep.endDate.split('T')[0]);
    }
    setView('edit');
  };

  const handleDeleteSaved = async (id) => {
    if (!window.confirm('Apagar este relatório?')) return;
    try {
      await franchiseV2Api.deleteReport(id);
      await loadSavedReports();
      toast.success('Relatório apagado');
    } catch { toast.error('Erro ao apagar'); }
  };

  const toggleMeta = (id) =>
    setSelectedMetaIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const updateMapping = (unityName, field, value) =>
    setMappings(prev => prev.map(m => m.unityName === unityName ? { ...m, [field]: value } : m));

  const toggleMetaInMapping = (unityName, metaId) =>
    setMappings(prev => prev.map(m => {
      if (m.unityName !== unityName) return m;
      const ids = m.metaAccountIds.includes(metaId)
        ? m.metaAccountIds.filter(x => x !== metaId)
        : [...m.metaAccountIds, metaId];
      return { ...m, metaAccountIds: ids };
    }));

  const availableMetaAccounts = metaAccounts.map(acc => ({
    id: acc.account_id || acc.metaAccountId || acc.id.replace('act_', ''),
    name: acc.name || acc.account_name || acc.id
  }));

  /* ─── LIST view ─────────────────────────────────────────────────────────── */
  if (view === 'list') {
    return (
      <div className="animate-fade-in pb-16 pt-8 px-4 lg:px-8 max-w-7xl mx-auto w-full">
        <header className="mb-8 flex justify-between items-center flex-wrap gap-4">
          <div>
            <h1 className="text-4xl lg:text-5xl font-extrabold font-headline tracking-tight text-on-surface mb-2">
              Franquia V2
            </h1>
            <p className="text-secondary font-body">Relatórios combinados Meta Ads + Clinicorp por unidade.</p>
          </div>
          <button
            onClick={() => { setCurrentReportId(null); setReportName('Relatório Franquia V2'); setClinicorpAccountId(''); setSelectedMetaIds([]); setMappings([]); setMappingsLoaded(false); setDatePreset('current_month'); setView('edit'); }}
            className="primary-pill-gradient text-white px-6 py-3 rounded-full font-bold shadow-lg hover:scale-105 transition-all flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-sm">add</span> Novo Relatório
          </button>
        </header>

        {savedReports.length === 0 ? (
          <div className="bg-white rounded-2xl p-16 text-center border border-dashed border-slate-200 text-slate-400">
            <span className="material-symbols-outlined text-5xl mb-3 opacity-30">merge_type</span>
            <p className="font-medium">Nenhum relatório salvo.</p>
            <p className="text-sm mt-1">Crie um relatório para combinar dados de Meta Ads e Clinicorp.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {savedReports.map(rep => (
              <div key={rep.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col gap-3">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-headline font-bold text-slate-800">{rep.name}</h3>
                  <button onClick={() => handleDeleteSaved(rep.id)} className="text-slate-300 hover:text-red-500 transition-colors shrink-0">
                    <span className="material-symbols-outlined text-[18px]">delete</span>
                  </button>
                </div>
                <p className="text-xs text-slate-400">
                  {rep.clinicorpAccount?.name || '—'} · {rep.datePreset || 'Personalizado'}
                </p>
                <button
                  onClick={() => handleOpenSaved(rep)}
                  className="mt-auto w-full py-2.5 rounded-xl bg-slate-100 hover:bg-primary/10 hover:text-primary text-slate-600 font-bold text-sm transition-colors flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined text-[16px]">open_in_new</span> Abrir
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ─── RESULT view ───────────────────────────────────────────────────────── */
  if (view === 'result' && resultData) {
    const clinicDbIds = selectedMetaIds
      .map(id => metaAccounts.find(a => (a.account_id || a.metaAccountId) === id || a.id?.replace('act_', '') === id)?.id)
      .filter(Boolean);

    return (
      <div className="animate-fade-in pb-16 pt-8 px-4 lg:px-8 max-w-7xl mx-auto w-full">
        <header className="mb-8">
          <h1 className="text-4xl font-extrabold font-headline tracking-tight text-on-surface mb-1">{reportName}</h1>
          <p className="text-secondary font-body text-sm">{resultPeriod?.startDate} → {resultPeriod?.endDate}</p>
        </header>
        <ResultView 
          units={resultData} 
          period={resultPeriod} 
          topCampaigns={resultTopCampaigns}
          topAds={resultTopAds}
          onBack={() => setView('edit')} 
          onShare={handleShareReport}
          sharedLink={sharedLink}
          clinicDbIds={clinicDbIds}
        />
      </div>
    );
  }

  /* ─── EDIT view ─────────────────────────────────────────────────────────── */
  return (
    <div className="animate-fade-in pb-16 pt-8 px-4 lg:px-8 max-w-7xl mx-auto w-full">
      <header className="mb-8 flex justify-between items-center flex-wrap gap-4">
        <div>
          <button onClick={() => setView('list')} className="flex items-center gap-1 text-slate-400 hover:text-primary text-xs font-medium mb-2 transition-colors">
            <span className="material-symbols-outlined text-[14px]">arrow_back</span> Todos os relatórios
          </button>
          <h1 className="text-3xl font-extrabold font-headline tracking-tight text-on-surface">
            {currentReportId ? 'Editar Relatório' : 'Novo Relatório Franquia V2'}
          </h1>
        </div>
        <div className="flex gap-2">
          <button onClick={handleSaveReport}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full font-bold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors shadow-sm text-sm"
          >
            <span className="material-symbols-outlined text-sm">save</span> Salvar
          </button>
          <button onClick={handleGenerate} disabled={generating}
            className="primary-pill-gradient text-white px-6 py-2.5 rounded-full font-bold shadow-lg hover:scale-105 transition-all flex items-center gap-2 disabled:opacity-50 disabled:hover:scale-100 text-sm"
          >
            {generating
              ? <><span className="material-symbols-outlined animate-spin text-sm">refresh</span> Gerando...</>
              : <><span className="material-symbols-outlined text-sm">play_arrow</span> Gerar Relatório</>
            }
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left: Config */}
        <div className="xl:col-span-1 space-y-5">
          {/* Name */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">
            <h2 className="font-headline font-bold text-slate-700 text-sm uppercase tracking-wider">Configuração</h2>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Nome</label>
              <input className="input w-full text-sm" value={reportName} onChange={e => setReportName(e.target.value)} />
            </div>

            {/* Clinicorp account */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Conta Clinicorp</label>
              <select className="input w-full text-sm" value={clinicorpAccountId} onChange={e => setClinicorpAccountId(e.target.value)}>
                <option value="">Selecione...</option>
                {clinicorpAccounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>

            {/* Period */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Período</label>
              <div className="grid grid-cols-3 gap-1.5 mb-2">
                {DATE_PRESETS.filter(p => p.value !== 'custom').map(p => (
                  <button key={p.value} type="button"
                    onClick={() => setDatePreset(p.value)}
                    className={`py-2 px-1 text-[11px] font-medium rounded-lg transition-all ${datePreset === p.value ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  >{p.label}</button>
                ))}
                <button type="button"
                  onClick={() => setDatePreset('custom')}
                  className={`py-2 px-1 text-[11px] font-medium rounded-lg transition-all ${datePreset === 'custom' ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                >Personalizado</button>
              </div>
              {datePreset === 'custom' && (
                <div className="grid grid-cols-2 gap-2">
                  <input type="date" className="input text-sm" value={startDate} onChange={e => setStartDate(e.target.value)} />
                  <input type="date" className="input text-sm" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>
              )}
            </div>

            {/* Load units button */}
            <button onClick={handleLoadUnits} disabled={!clinicorpAccountId || loading}
              className="w-full py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading
                ? <><span className="material-symbols-outlined animate-spin text-sm">refresh</span> Carregando...</>
                : <><span className="material-symbols-outlined text-sm">download</span> Carregar Unidades</>
              }
            </button>
          </div>

          {/* Meta accounts multi-select */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <h2 className="font-headline font-bold text-slate-700 text-sm uppercase tracking-wider mb-3">
              Contas Meta Disponíveis
            </h2>
            <div
              className="w-full bg-slate-50 px-4 py-3 rounded-xl text-sm cursor-pointer flex justify-between items-center mb-2"
              onClick={() => setMetaDropOpen(p => !p)}
            >
              <span className="text-slate-600">{selectedMetaIds.length === 0 ? 'Selecionar contas...' : `${selectedMetaIds.length} selecionada(s)`}</span>
              <span className="material-symbols-outlined text-slate-400">{metaDropOpen ? 'expand_less' : 'expand_more'}</span>
            </div>
            {metaDropOpen && (
              <div className="max-h-48 overflow-y-auto space-y-1 border border-slate-100 rounded-xl p-2">
                {availableMetaAccounts.map(acc => (
                  <label key={acc.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer text-xs">
                    <input type="checkbox" checked={selectedMetaIds.includes(acc.id)} onChange={() => toggleMeta(acc.id)} className="accent-primary" />
                    <span className="text-slate-700 truncate">{acc.name}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="text-[10px] text-slate-400 mt-2">Selecione as contas para disponibilizar no mapeamento</p>
          </div>
        </div>

        {/* Right: Mapping table */}
        <div className="xl:col-span-2">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
              <h2 className="font-headline font-bold text-slate-700 flex items-center gap-2">
                <span className="material-symbols-outlined text-primary text-[20px]">device_hub</span>
                Mapeamento de Unidades
                <span className="text-xs font-normal text-slate-400 ml-1">({mappings.filter(m => !m.ignored).length} ativas)</span>
              </h2>
              <button onClick={handleSaveMappings} disabled={savingMappings || mappings.length === 0}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold bg-slate-100 hover:bg-primary/10 hover:text-primary text-slate-600 transition-colors disabled:opacity-50"
              >
                {savingMappings ? <><span className="material-symbols-outlined animate-spin text-[14px]">refresh</span> Salvando...</> : <><span className="material-symbols-outlined text-[14px]">save</span> Salvar Mapeamentos</>}
              </button>
            </div>

            {mappings.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <span className="material-symbols-outlined text-4xl mb-2 opacity-30">device_hub</span>
                <p className="text-sm">Selecione uma conta Clinicorp e clique em "Carregar Unidades".</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    <tr>
                      <th className="px-4 py-3 text-left">Unidade Clinicorp</th>
                      <th className="px-4 py-3 text-left">Conta(s) Meta</th>
                      <th className="px-4 py-3 text-left w-44">Página (Leads LP)</th>
                      <th className="px-4 py-3 text-center w-20">Ignorar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {mappings.map((mapping) => (
                      <tr key={mapping.unityName} className={mapping.ignored ? 'opacity-40' : ''}>
                        <td className="px-4 py-3 text-slate-800 font-medium text-xs whitespace-nowrap">
                          {mapping.unityName}
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            {/* Selected badges */}
                            {mapping.metaAccountIds.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {mapping.metaAccountIds.map(id => {
                                  const acc = availableMetaAccounts.find(a => a.id === id);
                                  return (
                                    <span key={id}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[10px] font-medium"
                                    >
                                      {acc?.name || id}
                                      <button onClick={() => toggleMetaInMapping(mapping.unityName, id)} className="hover:text-red-500 transition-colors">×</button>
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                            {/* Dropdown to add */}
                            {!mapping.ignored && (
                              <select
                                className="text-[11px] text-slate-500 bg-transparent border border-dashed border-slate-300 rounded-lg px-2 py-1 w-full max-w-xs cursor-pointer hover:border-primary transition-colors"
                                value=""
                                onChange={(e) => { if (e.target.value) toggleMetaInMapping(mapping.unityName, e.target.value); }}
                              >
                                <option value="">+ Adicionar conta Meta...</option>
                                {availableMetaAccounts
                                  .filter(a => !mapping.metaAccountIds.includes(a.id))
                                  .map(a => (
                                    <option key={a.id} value={a.id}>{a.name}</option>
                                  ))}
                              </select>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            className="text-[11px] text-slate-600 bg-transparent border border-slate-200 rounded-lg px-2 py-1.5 w-full cursor-pointer hover:border-primary transition-colors"
                            value={mapping.pageId || ''}
                            onChange={(e) => {
                              const pg = pages.find(p => p.id === e.target.value);
                              updateMapping(mapping.unityName, 'pageId', e.target.value);
                              updateMapping(mapping.unityName, 'pageName', pg?.name || '');
                            }}
                          >
                            <option value="">Sem página</option>
                            {pages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={mapping.ignored}
                            onChange={e => updateMapping(mapping.unityName, 'ignored', e.target.checked)}
                            className="accent-red-500 w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
